import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { supabase } from "@/lib/supabase";
import RichTextEditor from "@/components/base/RichTextEditor";
import ImageUploader from "@/components/base/ImageUploader";
import MultiImageUploader from "@/components/base/MultiImageUploader";
import type { GalleryImage } from "@/components/base/MultiImageUploader";

const sectors = ["All", "Health", "Education", "Economic Security", "Protection"];
const statuses = ["All", "Active", "Completed", "Planned"];

const sectorColors: Record<string, string> = {
  Health: "bg-emerald-100 text-emerald-700",
  Education: "bg-amber-100 text-amber-700",
  "Economic Security": "bg-teal-100 text-teal-700",
  Protection: "bg-rose-100 text-rose-700",
};

const statusColors: Record<string, string> = {
  Active: "bg-emerald-100 text-emerald-700",
  Completed: "bg-stone-100 text-stone-600",
  Planned: "bg-amber-100 text-amber-700",
};

type FormData = {
  title: string;
  sector: string;
  location: string;
  status: "Active" | "Completed" | "Planned";
  startDate: string;
  endDate: string;
  beneficiaries: string;
  description: string;
  imageUrl: string;
  content: string;
  galleryImages: GalleryImage[];
};

const emptyForm: FormData = {
  title: "",
  sector: "Health",
  location: "",
  status: "Active",
  startDate: "",
  endDate: "",
  beneficiaries: "",
  description: "",
  imageUrl: "",
  content: "",
  galleryImages: [],
};

export default function AdminProjectsPage() {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterSector, setFilterSector] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(0);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching projects:", error);
    } else {
      setProjects(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const filtered = projects.filter((p) => {
    const matchSector = filterSector === "All" || p.sector === filterSector;
    const matchStatus = filterStatus === "All" || p.status === filterStatus;
    const matchSearch =
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      (p.location || "").toLowerCase().includes(search.toLowerCase());
    return matchSector && matchStatus && matchSearch;
  });

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setCharCount(0);
    setShowModal(true);
  };

  const openEdit = async (p: any) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      sector: p.sector,
      location: p.location || "",
      status: p.status,
      startDate: p.start_date ? new Date(p.start_date).toISOString().split("T")[0] : "",
      endDate: p.end_date ? new Date(p.end_date).toISOString().split("T")[0] : "",
      beneficiaries: p.beneficiaries || "",
      description: p.description || "",
      imageUrl: p.image_url || "",
      content: p.content || "",
      galleryImages: [],
    });

    // Load gallery images
    const { data: galleryData } = await supabase
      .from("project_images")
      .select("*")
      .eq("project_id", p.id)
      .order("sort_order", { ascending: true });

    if (galleryData && galleryData.length > 0) {
      setForm((prev) => ({
        ...prev,
        galleryImages: galleryData.map((img: any) => ({
          id: img.id,
          url: img.image_url,
          caption: img.caption || "",
        })),
      }));
    }

    setCharCount((p.description || "").length);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;

    let projectId = editingId;

    if (editingId !== null) {
      const { error } = await supabase
        .from("projects")
        .update({
          title: form.title,
          sector: form.sector,
          location: form.location,
          status: form.status,
          start_date: form.startDate || null,
          end_date: form.endDate || null,
          beneficiaries: form.beneficiaries,
          description: form.description,
          image_url: form.imageUrl,
          content: form.content,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingId);
      if (error) {
        console.error("Error updating project:", error);
        return;
      }
    } else {
      const { data, error } = await supabase
        .from("projects")
        .insert({
          title: form.title,
          sector: form.sector,
          location: form.location,
          status: form.status,
          start_date: form.startDate || null,
          end_date: form.endDate || null,
          beneficiaries: form.beneficiaries,
          description: form.description,
          image_url: form.imageUrl,
          content: form.content,
        })
        .select()
        .single();
      if (error) {
        console.error("Error creating project:", error);
        return;
      }
      projectId = data.id;
    }

    // Sync gallery images
    if (projectId) {
      // Delete existing images not in current list
      const currentImageIds = form.galleryImages
        .filter((img) => img.id)
        .map((img) => img.id);
      if (currentImageIds.length > 0) {
        const { data: existing } = await supabase
          .from("project_images")
          .select("id")
          .eq("project_id", projectId);
        const existingIds = existing?.map((e) => e.id) || [];
        const toDelete = existingIds.filter((id) => !currentImageIds.includes(id));
        if (toDelete.length > 0) {
          await supabase.from("project_images").delete().in("id", toDelete);
        }
      } else {
        // No images with IDs, delete all for this project
        await supabase.from("project_images").delete().eq("project_id", projectId);
      }

      // Upsert current images
      for (let i = 0; i < form.galleryImages.length; i++) {
        const img = form.galleryImages[i];
        if (img.id) {
          // Update existing
          await supabase
            .from("project_images")
            .update({
              image_url: img.url,
              caption: img.caption || null,
              sort_order: i,
            })
            .eq("id", img.id);
        } else if (img.url.trim()) {
          // Insert new
          await supabase.from("project_images").insert({
            project_id: projectId,
            image_url: img.url,
            caption: img.caption || null,
            sort_order: i,
          });
        }
      }
    }

    setShowModal(false);
    fetchProjects();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("projects").delete().eq("id", id);
    if (error) {
      console.error("Error deleting project:", error);
      return;
    }
    setDeleteId(null);
    fetchProjects();
  };

  return (
    <AdminLayout title="Projects" subtitle="Manage all projects across the four core sectors">
      {/* Sector summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {["Health", "Education", "Economic Security", "Protection"].map((s) => {
          const count = projects.filter((p) => p.sector === s).length;
          const active = projects.filter((p) => p.sector === s && p.status === "Active").length;
          const icons: Record<string, string> = {
            Health: "ri-heart-pulse-line",
            Education: "ri-book-open-line",
            "Economic Security": "ri-seedling-line",
            Protection: "ri-shield-check-line",
          };
          return (
            <div
              key={s}
              className="bg-white rounded-2xl p-4 border border-stone-100 cursor-pointer hover:border-primary/30 transition-all duration-200"
              onClick={() => setFilterSector(s)}
            >
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-3 ${sectorColors[s]}`}>
                <i className={`${icons[s]} text-base`}></i>
              </div>
              <p className="text-xl font-bold text-stone-800">{count}</p>
              <p className="text-xs font-medium text-stone-600 mt-0.5">{s}</p>
              <p className="text-xs text-stone-400 mt-1">{active} active</p>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-primary text-stone-700 placeholder-stone-400"
          />
        </div>
        <select
          value={filterSector}
          onChange={(e) => setFilterSector(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 focus:outline-none focus:border-primary cursor-pointer"
        >
          {sectors.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 focus:outline-none focus:border-primary cursor-pointer"
        >
          {statuses.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors duration-200 whitespace-nowrap cursor-pointer ml-auto"
        >
          <i className="ri-add-line"></i>
          New Project
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-primary text-2xl"></i>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Project</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">Sector</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">Location</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">Beneficiaries</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-stone-50/50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-stone-800">{p.title}</p>
                      <p className="text-xs text-stone-400 mt-0.5">{p.start_date} &mdash; {p.end_date}</p>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${sectorColors[p.sector] ?? "bg-stone-100 text-stone-500"}`}>
                        {p.sector}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span className="text-sm text-stone-600">{p.location}</span>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span className="text-sm font-semibold text-stone-700">{p.beneficiaries}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[p.status]}`}>{p.status}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(p)}
                          className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-primary hover:bg-primary/10 rounded-lg cursor-pointer transition-all duration-200"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteId(p.id)}
                          className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-all duration-200"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-3">
                  <i className="ri-folder-chart-line text-stone-400 text-xl"></i>
                </div>
                <p className="text-stone-500 text-sm font-medium">No projects found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
              <h2 className="text-base font-bold text-stone-800">{editingId ? "Edit Project" : "New Project"}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Project Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Project title"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Northwest Region"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Sector</label>
                  <select
                    value={form.sector}
                    onChange={(e) => setForm({ ...form, sector: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer"
                  >
                    {["Health", "Education", "Economic Security", "Protection"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value as "Active" | "Completed" | "Planned" })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer"
                  >
                    <option>Active</option>
                    <option>Planned</option>
                    <option>Completed</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 cursor-pointer"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">End Date</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 cursor-pointer"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Beneficiaries</label>
                  <input
                    type="text"
                    value={form.beneficiaries}
                    onChange={(e) => setForm({ ...form, beneficiaries: e.target.value })}
                    placeholder="e.g. 2,500+"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Cover Image</label>
                <ImageUploader
                  value={form.imageUrl}
                  onChange={(url) => setForm({ ...form, imageUrl: url })}
                  placeholder="Paste URL or upload an image"
                />
              </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Short Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => { setForm({ ...form, description: e.target.value }); setCharCount(e.target.value.length); }}
                  rows={2}
                  maxLength={500}
                  placeholder="Brief project description for cards and listings..."
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400 resize-none"
                />
                <p className="text-xs text-stone-400 text-right">{charCount}/500</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Full Project Content</label>
                <RichTextEditor
                  value={form.content}
                  onChange={(html) => setForm({ ...form, content: html })}
                  placeholder="Write the full project description, objectives, impact details..."
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Gallery Images</label>
                <MultiImageUploader
                  images={form.galleryImages}
                  onChange={(images) => setForm({ ...form, galleryImages: images })}
                  projectName={form.title || "new-project"}
                  maxImages={20}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-100">
              <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-100 cursor-pointer transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={!form.title.trim()} className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary-dark cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {editingId ? "Save Changes" : "Create Project"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-stone-800 text-center mb-2">Delete Project?</h3>
            <p className="text-sm text-stone-500 text-center mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-stone-600 border border-stone-200 hover:bg-stone-50 cursor-pointer transition-colors">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 cursor-pointer transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}